import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles, Typography } from '@material-ui/core';
import axios from 'axios';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import LoaderButton from './LoaderButton';
import { getYearGroups } from '../helpers/ticket';

const styles = {
    root: {
        marginTop: '20px',
        minHeight: '100px',
        marginBottom: '20px',
        padding: '0 10px 10px',
        textAlign: 'center'
    },
    card: {
        margin: '30px auto 0 auto',
        maxWidth: '360px',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        padding: '10px 0 10px 8px'
    },
    button: {
        margin: '20px 0 20px',
        width: '200px',
        height: '56px'
    },
    error: {
        margin: '10px 20px 0px',
        color: red[500],
        minHeight: '20px'
    }
};

class DirectDebitPaymentForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            processing: false,
            error: ''
        };
    }

    handleSubmit = async () => {
        const {
            history,
            ticket,
            service,
            renewal,
            client,
            stop,
            dest,
            voucher,
            yr,
            zone,
            zoneStop,
            extId
        } = this.props;
        this.setState({
            processing: true,
            error: ''
        });

        if (
            client &&
            client.features &&
            client.features.require_id === 'student' &&
            !extId
        ) {
            this.setState({
                processing: false,
                error: 'Please provide a student id'
            });
            return;
        }

        const useZones =
            service.zones &&
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_prices;

        if (useZones) {
            const serviceZone =
                zone && service.zones.find(z => z.name === zone);

            if (!serviceZone) {
                this.setState({
                    processing: false,
                    error: 'Please select a zone'
                });
                return;
            }

            if (
                serviceZone.stops &&
                serviceZone.stops.length &&
                !serviceZone.stops.includes(zoneStop)
            ) {
                this.setState({
                    processing: false,
                    error: 'Please select a bus stop'
                });
                return;
            }
        } else if (
            service &&
            'stops' in service &&
            !service.stops.includes(stop)
        ) {
            this.setState({
                processing: false,
                error: 'Please select a bus stop'
            });
            return;
        }

        if (service && service.destinations && service.destinations.length) {
            if (!service.destinations.includes(dest)) {
                this.setState({
                    processing: false,
                    error: 'Please select a destination'
                });
                return;
            }
        }

        if (service && getYearGroups(service.yr_groups).length) {
            if (!yr) {
                this.setState({
                    processing: false,
                    error: 'Please select a year group'
                });
                return;
            }
        }

        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const data = {
                renewal,
                ticket,
                service,
                client,
                stop,
                voucher
            };

            if (extId) {
                data.ext_id = extId;
            }

            if (service && getYearGroups(service.yr_groups).length) {
                data.yr = yr;
            }

            if (useZones) {
                data.zone = zone;
                data.zoneStop = zoneStop;
            }

            if (
                service &&
                service.destinations &&
                service.destinations.length
            ) {
                data.dest = dest;
            }

            const response = await axios.post(
                `${
                    process.env.REACT_APP_PAYMENT_API_ENDPOINT
                }/customer-ticket/gocardless/renew`,
                data,
                {
                    headers
                }
            );

            if (response.data.id) {
                history.push(`/renewal-confirmed/${response.data.id}`);
                // @NOTE: Hack to jump user to top of page on submit, as the route doesn't change
                // resulting in unexpected landing half way down subsequent view.
                window.scrollTo(0, 0);
            } else {
                throw new Error('No ticket id generated.');
            }
        } catch (e) {
            console.error(e);
            this.setState({
                processing: false,
                error:
                    'There was a problem processing your payment, please try again later'
            });
        }
    };

    render() {
        const { classes, client, service } = this.props;
        const { processing, error } = this.state;

        return (
            <div className={classes.root}>
                <Typography variant="body1" style={{ paddingBottom: '1em' }}>
                    By completing this purchase, I confirm I have read and
                    accepted the following:
                </Typography>
                {service.terms_url && (
                    <Typography variant="body1">
                        <a
                            href={service.terms_url}
                            rel="noopener noreferrer"
                            target="_blank"
                            style={{ color: '#000' }}
                        >
                            {client.name} T&amp;Cs
                        </a>
                    </Typography>
                )}
                <Typography variant="body1">
                    <a
                        href="https://shuttleid.uk/static/shuttleid-terms-of-use-passengers,-purchasers-and-other-users-updated-august-2020-8c80ca7b2c819e755e149871e5a98e88.pdf"
                        rel="noopener noreferrer"
                        target="_blank"
                        style={{ color: '#000' }}
                    >
                        ShuttleID T&amp;Cs
                    </a>
                </Typography>
                {error && (
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                )}
                <LoaderButton
                    className={`${classes.button} pay-btn`}
                    variant="outlined"
                    color="primary"
                    type="submit"
                    isLoading={processing}
                    text="Confirm and renew"
                    loadingText="Confirming..."
                    onClick={this.handleSubmit}
                />
            </div>
        );
    }
}

DirectDebitPaymentForm.propTypes = {
    classes: PropTypes.shape().isRequired,
    ticket: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    renewal: PropTypes.shape().isRequired,
    client: PropTypes.shape().isRequired,
    stop: PropTypes.string,
    dest: PropTypes.string,
    extId: PropTypes.string
};

DirectDebitPaymentForm.defaultProps = {
    stop: '',
    dest: '',
    extId: ''
};

export default withStyles(styles)(withRouter(DirectDebitPaymentForm));
